import React from 'react';
import WeekCalendar from './WeekCalendar'; // Adjust path as needed
import './graphStyles.scss';

export function GraphDropDown({ display, currentWeek, setCurrentWeek, weeks}) {
    // Debug logging
    console.log('GraphDropDown received weeks:', weeks, 'currentWeek index:', currentWeek);
    console.log('weeks length:', weeks?.length, 'weeks type:', typeof weeks, 'is array:', Array.isArray(weeks));
    
    // Convert weeks array to the format expected by WeekCalendar (YYYYMMDD format)
    const convertWeeksToCalendarFormat = (weeksArray) => {
        if (!weeksArray || !Array.isArray(weeksArray)) {
            console.log('No valid weeks array provided:', weeksArray);
            return [];
        }
        
        console.log('Converting weeks array:', weeksArray);
        
        const converted = weeksArray.map((week, index) => {
            try {
                // Convert to string and trim any whitespace
                let weekStr = week.toString().trim();
                console.log(`[${index}] Original week: "${weekStr}" (length: ${weekStr.length})`);
                
                // Remove all non-digit characters
                let cleanWeek = weekStr.replace(/\D/g, '');
                console.log(`[${index}] After removing non-digits: "${cleanWeek}" (length: ${cleanWeek.length})`);
                
                // Check if it's a valid YYYYMMDD format
                if (cleanWeek.length === 8 && /^\d{8}$/.test(cleanWeek)) {
                    // Additional validation: check if it's a valid date
                    const year = parseInt(cleanWeek.substring(0, 4));
                    const month = parseInt(cleanWeek.substring(4, 6));
                    const day = parseInt(cleanWeek.substring(6, 8));
                    
                    // Basic date validation
                    if (year >= 2020 && year <= 2030 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                        const result = parseInt(cleanWeek);
                        console.log(`[${index}] ✅ Successfully converted: ${weekStr} -> ${result}`);
                        return result;
                    } else {
                        console.warn(`[${index}] ❌ Invalid date values: Y:${year} M:${month} D:${day}`);
                    }
                } else {
                    console.warn(`[${index}] ❌ Invalid format after cleaning: "${cleanWeek}" (length: ${cleanWeek.length})`);
                }
                
                return null;
            } catch (error) {
                console.warn(`[${index}] ❌ Error converting week:`, week, error);
                return null;
            }
        }).filter(week => week !== null);
        
        console.log('Final converted weeks:', converted);
        return converted;
    };

    // Convert current week index to the actual week value for calendar
    const getCurrentWeekValue = () => {
        if (weeks?.length > 0 && typeof currentWeek === 'number' && currentWeek >= 0 && currentWeek < weeks.length) {
            try {
                const weekStr = weeks[currentWeek].toString().trim();
                const cleanWeek = weekStr.replace(/\D/g, '');
                
                console.log('Current week string:', weekStr, '-> cleaned:', cleanWeek, 'from index:', currentWeek);
                
                if (cleanWeek.length === 8 && /^\d{8}$/.test(cleanWeek)) {
                    const value = parseInt(cleanWeek);
                    console.log('Current week value for calendar:', value);
                    return value;
                } else {
                    console.warn('Current week format invalid:', weeks[currentWeek], 'cleaned:', cleanWeek, 'length:', cleanWeek.length);
                }
            } catch (error) {
                console.warn('Error getting current week value:', error);
            }
        }
        console.log('No valid current week, returning null');
        return null;
    };

    // Handle week selection from calendar
    const handleWeekSelection = (selectedWeekValue) => {
        if (!selectedWeekValue || !weeks?.length) return;
        
        console.log('Week selected from calendar:', selectedWeekValue);
        
        // Find the index of the selected week in the weeks array
        const weekIndex = weeks.findIndex(week => {
            try {
                const weekStr = week.toString().trim();
                const cleanWeek = weekStr.replace(/\D/g, '');
                const convertedValue = parseInt(cleanWeek);
                console.log(`Comparing: ${selectedWeekValue} === ${convertedValue} (from ${weekStr})`);
                return convertedValue === selectedWeekValue;
            } catch (error) {
                console.warn('Error comparing week:', week, error);
                return false;
            }
        });
        
        console.log('Found week index:', weekIndex);
        
        if (weekIndex !== -1) {
            setCurrentWeek(weekIndex);
        } else {
            console.warn('Could not find week index for selected value:', selectedWeekValue);
        }
    };

    // Hide component for dashboard display  
    if (display === "dashboard") {
        return <div className="hidden"></div>;
    }

    // Show loading state if weeks is empty or undefined
    if (!weeks || weeks.length === 0) {
        console.log('No weeks available, showing placeholder');
        return (
            <div className="visible">
                <div className="inline-flex w-full justify-between items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-400 border border-gray-200">
                    Loading weeks...
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                    </svg>
                </div>
            </div>
        );
    }

    const calendarWeeks = convertWeeksToCalendarFormat(weeks);
    const currentWeekValue = getCurrentWeekValue();

    console.log('Final calendar weeks:', calendarWeeks, 'Current week value for calendar:', currentWeekValue);

    // If no valid weeks after conversion, show error state
    if (calendarWeeks.length === 0) {
        console.warn('No valid weeks after conversion');
        return (
            <div className="visible">
                <div className="inline-flex w-full justify-between items-center rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 border border-red-200">
                    Invalid date format
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                </div>
            </div>
        );
    }

    return (
        <div className="visible">
            <WeekCalendar
                weeks={calendarWeeks}
                currentWeek={currentWeekValue}
                setCurrentWeek={handleWeekSelection}
                placeholder="Select Date"
                width={display === "firstlasttouch" ? "200px" : "280px"}
                disabled={false}
            />
        </div>
    );
}